import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { onOrgReferrals } from "../services/intakeService";
import BottomNav from "../components/BottomNav";
import {
  Calendar, UserPlus, ClipboardList, Phone,
  ChevronRight, Clock, Users, FileCheck,
} from "lucide-react";

export default function FrontDeskDashboard() {
  const navigate = useNavigate();
  const { profile, user } = useAuth();

  const displayName = profile?.displayName || user?.displayName || "Front Desk";
  const firstName = displayName.split(" ")[0];
  const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";

  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    const unsub = onOrgReferrals(org, setReferrals);
    return unsub;
  }, [org]);

  const newReferrals = referrals.filter(r => r.status === "new").length;
  const pendingIntakes = referrals.filter(r => r.status === "scheduled").length;

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const C = {
    card: {
      background: "rgba(15,23,42,0.7)",
      border: "1px solid rgba(99,179,237,0.08)",
      borderRadius: 16,
      padding: 20,
      marginBottom: 12,
    },
  };

  const quickActions = [
    {
      label: "Today's Schedule",
      icon: Calendar,
      color: "#63b3ed",
      path: "/schedule",
    },
    {
      label: "New Intake",
      icon: UserPlus,
      color: "#f6ad55",
      path: "/intake",
    },
    {
      label: "Referrals",
      icon: ClipboardList,
      color: "#4fd1c5",
      path: "/referrals",
      badge: newReferrals > 0 ? `${newReferrals} new` : null,
    },
    {
      label: "All Clients",
      icon: Users,
      color: "#68d391",
      path: "/caseload",
    },
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0f1a",
      color: "#e2e8f0",
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      paddingBottom: 100,
    }}>
      {/* Header */}
      <header style={{
        padding: "20px 20px 16px",
        background: "linear-gradient(135deg, rgba(15,23,42,0.9), rgba(16,28,50,0.9))",
        borderBottom: "1px solid rgba(99,179,237,0.08)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, color: "#718096", fontWeight: 600 }}>{greeting()}</div>
            <div style={{ fontSize: 22, fontWeight: 900, marginTop: 2 }}>{firstName}</div>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#f6ad55",
              letterSpacing: 1.5, textTransform: "uppercase", marginTop: 4,
            }}>
              Front Desk
            </div>
          </div>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: "linear-gradient(135deg, #f6ad55, #dd6b20)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 900, color: "#0a0f1e",
          }}>
            {firstName[0]}
          </div>
        </div>
      </header>

      <main style={{ padding: 20 }}>
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Today", value: "—", color: "#63b3ed", sub: "appts" },
            { label: "Pending", value: pendingIntakes, color: "#f6ad55", sub: "intakes" },
            { label: "Referrals", value: newReferrals, color: "#4fd1c5", sub: "new" },
          ].map((s, i) => (
            <div key={i} style={{ ...C.card, textAlign: "center", padding: 16 }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div style={{ fontSize: 12, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10 }}>
          Quick Actions
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {quickActions.map((action, i) => {
            const Icon = action.icon;
            return (
              <button
                key={i}
                onClick={() => navigate(action.path)}
                style={{
                  ...C.card,
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  cursor: "pointer",
                  marginBottom: 0,
                  textAlign: "left",
                  width: "100%",
                }}
              >
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: `${action.color}12`,
                  border: `1px solid ${action.color}25`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Icon size={20} style={{ color: action.color }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0" }}>{action.label}</div>
                  {action.badge && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: "#f6ad55",
                      background: "rgba(246,173,85,0.1)",
                      padding: "2px 8px", borderRadius: 6,
                    }}>{action.badge}</span>
                  )}
                </div>
                <ChevronRight size={16} style={{ color: "#4a5568" }} />
              </button>
            );
          })}
        </div>
      </main>

      <BottomNav active="home" />
    </div>
  );
}